import { useEffect, useRef, useState } from "react"
import { IoCloudUploadOutline, IoArrowBack, IoClose } from "react-icons/io5"
import { useDispatch } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { AppDispatch } from "../../store"
import { uploadChatbotRule, updateChatbotRule, fetchChatbotRules } from "../../features/chatbot/ChatBotServices"
import PageLayout from "../../components/layouts/PageLayout"
import CustomNavbar from "../../components/common/CustomNavbar"
import { toast } from "sonner"
import PhoneInput from "react-phone-number-input";

export default function AddChatBot() {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEdit = Boolean(id)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [keyword, setKeyword] = useState("")
    const [responseText, setResponseText] = useState("")
    const [responseType, setResponseType] = useState("")
    const [mediaId, setMediaId] = useState("")
    const [file, setFile] = useState<File | null>(null)
    const [loading, setLoading] = useState(false)
    const [fetching, setFetching] = useState(isEdit)

    // Notify admin fields
    const [notifyAdmin, setNotifyAdmin] = useState<0 | 1>(1)
    const [notifyMobile, setNotifyMobile] = useState("")
    const [notifyMessage, setNotifyMessage] = useState("")

    useEffect(() => {
        if (!isEdit) return

        const load = async () => {
            const result = await dispatch(fetchChatbotRules({ page: 0, size: 100 }))
            if (fetchChatbotRules.fulfilled.match(result)) {
                const rule = result.payload.results.find((r) => String(r.id) === id)
                if (rule) {
                    setKeyword(rule.keyword)
                    setResponseText(rule.responseText || "")
                    setResponseType(rule.responseType || "")
                    setMediaId(rule.mediaId || "")
                    setNotifyAdmin(rule.notifyAdmin === 1 ? 1 : 0)
                    setNotifyMobile(rule.notifyMobile || "")
                    setNotifyMessage(rule.notifyMessage || "")
                } else {
                    toast.error("Rule not found")
                    navigate("/admin/chatbot")
                }
            }
            setFetching(false)
        }
        load()
    }, [id])

    const handleSubmit = async () => {
        if (!keyword.trim()) { toast.error("Keyword required"); return }
        if (!responseText.trim()) { toast.error("Response required"); return }
        if (notifyAdmin === 1 && !notifyMobile.trim()) { toast.error("Notify mobile number required"); return }
        if (notifyAdmin === 1 && !notifyMessage.trim()) { toast.error("Notify message required"); return }

        setLoading(true)

        const result = isEdit
            ? await dispatch(updateChatbotRule({
                id: Number(id),
                keyword,
                responseText,
                responseType,
                mediaId,
                notifyAdmin,
                notifyMobile: notifyAdmin === 1 ? notifyMobile : "",
                notifyMessage: notifyAdmin === 1 ? notifyMessage : "",
            }))
            : await dispatch(uploadChatbotRule({
                keyword,
                responseText,
                file,
                notifyAdmin,
                notifyMobile: notifyAdmin === 1 ? notifyMobile : "",
                notifyMessage: notifyAdmin === 1 ? notifyMessage : "",
            }))

        const action = isEdit ? updateChatbotRule : uploadChatbotRule

        if (action.fulfilled.match(result)) {
            toast.success(isEdit ? "Rule updated" : "Rule created")
            navigate("/admin/chatbot")
        } else {
            toast.error(isEdit ? "Failed to update rule" : "Failed to create rule")
        }
        setLoading(false)
    }

    return (
        <PageLayout
            customNavbar={
                <CustomNavbar>
                    <div className="flex gap-3 items-center w-max">
                        <IoArrowBack
                            className="text-2xl cursor-pointer hover:text-brand-main transition"
                            onClick={() => navigate(-1)}
                        />
                        <span className="font-bold text-lg uppercase">
                            {isEdit ? "Edit Chatbot Rule" : "Add Chatbot Rule"}
                        </span>
                    </div>
                </CustomNavbar>
            }
        >
            <div className="rounded-4xl w-full bg-gray-100">
                <div className="flex flex-col rounded-4xl w-full p-4 bg-white min-h-screen gap-6">

                    {/* Header */}
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">
                            {isEdit ? "Edit Rule" : "Create New Rule"}
                        </h2>
                        <p className="text-sm text-text-secondary mt-1">
                            Configure a keyword-based auto-reply for your chatbot
                        </p>
                    </div>

                    {fetching ? (
                        <div className="text-center py-16 text-gray-400">Loading rule...</div>
                    ) : (
                        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 flex flex-col gap-4">

                            <div className="flex flex-col md:flex-row gap-4">

                                {/* Keyword */}
                                <div className="flex flex-col gap-1 flex-1">
                                    <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Keyword *</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. pricing, support, refund"
                                        value={keyword}
                                        onChange={e => setKeyword(e.target.value)}
                                        className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium outline-none focus:border-brand-main transition-colors bg-white"
                                    />
                                    <p className="text-xs text-text-secondary">User types this → gets the response below</p>
                                </div>

                                {/* Response Text */}
                                <div className="flex flex-col gap-1 flex-[2]">
                                    <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Response Text *</label>
                                    <textarea
                                        placeholder="Enter the auto-reply message..."
                                        value={responseText}
                                        onChange={e => setResponseText(e.target.value)}
                                        rows={3}
                                        className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium outline-none focus:border-brand-main transition-colors bg-white resize-none"
                                    />
                                    <p className="text-xs text-text-secondary">{responseText.length} characters</p>
                                </div>
                            </div>

                            {/* Notify Admin Toggle */}
                            <div className="flex flex-col gap-3">
                                <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Notify Admin</label>
                                <div className="flex items-center gap-6">
                                    {/* Yes */}
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                            type="radio"
                                            name="notifyAdmin"
                                            checked={notifyAdmin === 1}
                                            onChange={() => setNotifyAdmin(1)}
                                            className="muber-input accent-brand-main w-4 h-4"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Yes</span>
                                    </label>
                                    {/* No */}
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                            type="radio"
                                            name="notifyAdmin"
                                            checked={notifyAdmin === 0}
                                            onChange={() => {
                                                setNotifyAdmin(0)
                                                setNotifyMobile("")
                                                setNotifyMessage("")
                                            }}
                                            className="muber-input accent-brand-main w-4 h-4"
                                        />
                                        <span className="text-sm font-medium text-gray-700">No</span>
                                    </label>
                                </div>

                                {/* Conditional fields — shown only when notifyAdmin = 1 */}
                                {notifyAdmin === 1 && (
                                    <div className="flex flex-col md:flex-row gap-4 mt-1 p-4 rounded-xl border border-blue-100 bg-blue-50/50">
                                        {/* Notify Mobile */}
                                        <div className="flex flex-col gap-2">
                                            <label className="font-bold">Mobile Number</label>
                                            <div className="border rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-brand-main">
                                                <PhoneInput
                                                    defaultCountry="IN"
                                                    international
                                                    withCountryCallingCode
                                                    countryCallingCodeEditable={false}
                                                    value={notifyMobile}
                                                    onChange={(value) => setNotifyMobile(value ?? "")}
                                                    placeholder="Enter mobile number"
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>

                                        {/* Notify Message */}
                                        <div className="flex flex-col gap-1 flex-[2]">
                                            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                                                Notify Message *
                                            </label>
                                            <textarea
                                                placeholder="Message to send to admin when this rule triggers..."
                                                value={notifyMessage}
                                                onChange={e => setNotifyMessage(e.target.value)}
                                                rows={3}
                                                className="muber-input border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium outline-none focus:border-brand-main transition-colors bg-white resize-none"
                                            />
                                            <p className="text-xs text-text-secondary">{notifyMessage.length} characters</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* File Upload — only on Add */}
                            {!isEdit && (
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Attachment (Optional)</label>
                                    <div
                                        className={`border-2 border-dashed rounded-xl p-5 flex flex-col items-center gap-2 cursor-pointer transition-colors
                                            ${file ? 'border-brand-main bg-blue-50' : 'border-gray-300 hover:border-brand-main bg-white'}`}
                                        onClick={() => fileInputRef.current?.click()}
                                        onDragOver={e => e.preventDefault()}
                                        onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f) }}
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            className="hidden"
                                            onChange={e => setFile(e.target.files?.[0] || null)}
                                        />
                                        <IoCloudUploadOutline className={`text-3xl ${file ? 'text-brand-main' : 'text-gray-400'}`} />
                                        {file ? (
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-semibold text-brand-main">{file.name}</span>
                                                <button
                                                    onClick={e => { e.stopPropagation(); setFile(null) }}
                                                    className="text-gray-400 hover:text-red-500"
                                                >
                                                    <IoClose />
                                                </button>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-text-secondary">
                                                Drag & drop or <span className="text-brand-main font-semibold">browse</span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="px-5 py-2.5 rounded-xl border border-gray-200 text-text-secondary font-semibold text-sm hover:bg-gray-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    className="px-6 py-2.5 rounded-xl bg-brand-blue-gradient text-white font-bold text-sm disabled:opacity-60 flex items-center gap-2"
                                >
                                    {loading
                                        ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                                        : isEdit ? "Update Rule" : "Save Rule"
                                    }
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </PageLayout>
    )
}